import React from 'react';

import SVG from 'components/base/SVG';
import { Priority } from 'app/PopupHolder';

import SaveInvestorButton from '../SaveInvestorButton';
import useSaveInvestor from '../useSaveInvestor';
import {
  getInvestorTypeColor,
  getInvestorTypeIcon,
  getInvestorTypeLabel,
  getInvestorTypeBackground,
} from '../investorTypeTheme';
import css from './style.scss';

// Identity header — Figma "contact banner", Type=investor (315:11023), inside
// the Investor Profile card (383:16287). Name + classification badge(s) sit
// over a decorative teal diagonal-stripe fill; the stripes carry no data, so
// they're reproduced as plain CSS rather than an exported asset. Geometry
// (left/top per stripe, rotate(44.5deg), 112x273 rectangles in a
// 271.234x273.218 flex box) is copied verbatim from Figma's own generated
// code rather than approximated, since the banner sits inside a 917px-wide,
// 90px-tall box with overflow hidden.
const STRIPES = [
  { left: 683, top: -33.5, color: '#94c9d1' },
  { left: 718, top: -14.5, color: '#b8dfe6' },
  { left: 738, top: 22.5, color: '#e2f7fa' },
];

// Classification colour/icon/label all come from the same per-type theme map
// the Investor Search card uses (../investorTypeTheme), so a badge reads
// identically whether it's seen on the card or the profile it opens into —
// including the label itself: the vendor's raw classification is shouting
// case ("CASH BUYER"), and `getInvestorTypeLabel` is what turns that into
// "Cash Buyer" (or title-cases an unthemed type) the same way the card's own
// `TypeTag` does; rendering `type` directly here left it in shouting case
// even with `text-transform: capitalize` on the badge, which only affects
// each word's first letter and does nothing to the rest of an all-caps word.
const ClassificationTag = ({ type }) => {
  const color = getInvestorTypeColor(type);
  const background = getInvestorTypeBackground(type);
  const icon = getInvestorTypeIcon(type);

  return (
    <span className={css.headerTag} style={{ color, backgroundColor: background }}>
      {!icon ? null : (
        <span className={css.headerTag__iconBox}>
          <SVG icon={icon} className={css.headerTag__glyph} />
        </span>
      )}
      {getInvestorTypeLabel(type)}
    </span>
  );
};

// "Individual" / "Company Owned" — Figma "other tags", Property 1=individual
// (299:10779). Verified/Unverified variants of this same component are
// deferred to Phase 3 per the S5 ticket, so only the entity-type reading is
// wired here.
const ENTITY_LABELS = {
  INDIVIDUAL: 'Individual',
  COMPANY: 'Company Owned',
};

const EntityTag = ({ entityType }) => {
  const label = ENTITY_LABELS[String(entityType || '').toUpperCase()];
  if (!label) return null;

  return <span className={css.entityTag}>{label}</span>;
};

const IdentityHeader = ({ investor, loading, onInvestorListModalToggle }) => {
  const classifications = investor.classifications || [];
  // Opened at Priority.HIGHEST rather than AddToMarketingListModal's own
  // MEDIUM default: this button lives inside the already-open Investor
  // Profile modal (also MEDIUM), and PopupHolder only ever renders the
  // front of a priority queue, so a same-tier popup would queue silently
  // behind it and never show — the same reason EditContactModal.js (a
  // sibling in this folder) opens at HIGHEST too.
  const { saved, toggleSave } = useSaveInvestor(investor, {
    priority: Priority.HIGHEST,
    onToggle: onInvestorListModalToggle,
  });

  // Opened by id from the Contacts Investor List only ever starts with a
  // thin {id, contactId} object -- no name until the ATAI-109 profile fetch
  // resolves (Search's "View Details" already has one immediately, so this
  // never shows there). While that's still in flight there's nothing real to
  // save yet either, so the bookmark button waits along with the name rather
  // than sitting there clickable against a still-loading investor.
  const isLoadingIdentity = !investor.name && loading;

  return (
    <div className={css.banner}>
      <div className={css.banner__stripes} aria-hidden="true">
        {STRIPES.map(({ left, top, color }) => (
          <span key={color} className={css.banner__stripeWrap} style={{ left, top }}>
            <span className={css.banner__stripe} style={{ backgroundColor: color }} />
          </span>
        ))}
      </div>
      {isLoadingIdentity ? (
        <div className={css.banner__loading}>
          <SVG icon="spinner" className={css.banner__loadingSpinner} />
        </div>
      ) : (
        <div className={css.banner__content}>
          <div className={css.banner__nameRow}>
            <p className={css.banner__name}>{investor.name || 'Unknown investor'}</p>
            <SaveInvestorButton
              saved={saved}
              onToggle={toggleSave}
              className={css.banner__saveButton}
              iconClassName={css.banner__saveIcon}
            />
          </div>
          {!classifications.length && !investor.entityType ? null : (
            <div className={css.banner__tags}>
              {classifications.map((type) => (
                <ClassificationTag key={type} type={type} />
              ))}
              <EntityTag entityType={investor.entityType} />
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default IdentityHeader;
